import type { BinaryAttachment, BinaryStorage } from "../types";
import type { BinaryStorageReadResult } from "../types";
import { BINARY_DEFAULT_MAX_BYTES } from "../contracts/runtimeTypes";

export async function boundedReadBinary(
  result: BinaryStorageReadResult,
  attachment: BinaryAttachment,
  maxBytes: number = BINARY_DEFAULT_MAX_BYTES,
): Promise<Uint8Array> {
  if (attachment.size > maxBytes) {
    throw new Error(
      `Binary attachment size ${attachment.size} bytes exceeds maxBytes ${maxBytes}. ` +
        `Raise the node's maxBytes setting if this document is expected to be larger.`,
    );
  }
  const out = new Uint8Array(attachment.size);
  const reader = result.body.getReader();
  let offset = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    if (!value) {
      continue;
    }
    if (offset + value.byteLength > out.byteLength) {
      throw new Error(`Binary stream produced more bytes than the attachment's declared size (${attachment.size}).`);
    }
    out.set(value, offset);
    offset += value.byteLength;
  }
  if (offset !== out.byteLength) {
    throw new Error(`Binary stream produced ${offset} bytes but attachment declared size ${attachment.size}.`);
  }
  return out;
}

export async function readBinaryAsBytes(
  storage: BinaryStorage,
  attachment: BinaryAttachment,
  maxBytes?: number,
): Promise<Uint8Array> {
  const result = await storage.openReadStream(attachment.storageKey);
  if (!result) {
    throw new Error("Binary attachment stream is unavailable.");
  }
  return boundedReadBinary(result, attachment, maxBytes);
}

export async function readBinaryAsText(
  storage: BinaryStorage,
  attachment: BinaryAttachment,
  maxBytes?: number,
): Promise<string> {
  const bytes = await readBinaryAsBytes(storage, attachment, maxBytes);
  return new TextDecoder().decode(bytes);
}

export async function readBinaryAsJson<T = unknown>(
  storage: BinaryStorage,
  attachment: BinaryAttachment,
  maxBytes?: number,
): Promise<T> {
  const text = await readBinaryAsText(storage, attachment, maxBytes);
  try {
    return JSON.parse(text) as T;
  } catch (cause) {
    throw new SyntaxError(
      `Binary attachment at storage key "${attachment.storageKey}" is not valid JSON: ${cause instanceof Error ? cause.message : String(cause)}`,
      { cause },
    );
  }
}
